import { t } from 'i18next'
import { useFormContext } from 'react-hook-form'
import { withMask } from 'use-mask-input'

import { Icon } from '@/components/icon'
import { Render } from '@/components/render-if'
import { Button, InputField, RadioGroupField } from '@/components/ui'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { ComboboxField } from '@/components/ui/combobox'
import {
  StepPropostaContent,
  StepPropostaFooter,
  StepPropostaHeaderContent,
  StepPropostaHeaderTitle,
  StepPropostaRoot,
} from '@/features/proposta/brasil/components/layouts'
import type {
  CompradorPessoaFisicaStepSchemaInput,
  CompradorPessoaFisicaStepSchemaOutput,
} from '@/features/proposta/brasil/domain/schemas/compradores/comprador-pf.schema'
import { useClientePorCpfProposta } from '@/features/proposta/brasil/hooks/use-cliente-por-cpf-proposta'
import { useClientePorId } from '@/features/proposta/brasil/hooks/use-cliente-por-id'
import { useDadosCompradorPf } from '@/features/proposta/brasil/hooks/use-dados-comprador-pf'
import type { TCompradoresCadastrados } from '@/features/proposta/brasil/types/proposta.type'
import type { TNacionalidade, TProfissao } from '@/types/cliente'
import { CPF_MASK } from '@/utils/masks'

const maiorDe18AnosOptions = [
  { value: 'sim', label: t('proposta:cadastro_compradores.steps.dados_comprador.maior_18_anos.sim') },
  { value: 'nao', label: t('proposta:cadastro_compradores.steps.dados_comprador.maior_18_anos.nao') },
]

type TDadosComprador = {
  onBack: () => void
  onNext: () => void
  onFinish: (value: CompradorPessoaFisicaStepSchemaOutput) => Promise<void>
  isLast: boolean
  compradores: TCompradoresCadastrados
  nacionalidades: TNacionalidade[]
  profissoes: TProfissao[]
  clienteAtendimento?: { id: string; cpf?: string | null }
  emailsJaVinculados: Record<string, string>
}

export const DadosCompradorPF = ({
  onBack,
  nacionalidades,
  profissoes,
  clienteAtendimento = { id: '', cpf: null },
  compradores,
  onNext,
  onFinish,
  isLast,
  emailsJaVinculados,
}: TDadosComprador) => {
  const {
    formState: { errors },
    handleSubmit,
    watch,
    control,
  } = useFormContext<CompradorPessoaFisicaStepSchemaInput, unknown, CompradorPessoaFisicaStepSchemaOutput>()
  const cpf = watch('dados.cpf')
  const cpfRaw = cpf?.replaceAll(/\D/g, '')
  const isEditing = watch('mode') === 'edit'
  const { refetch: buscarPorId } = useClientePorId(clienteAtendimento?.id ?? '', false)
  const { refetch: buscarPorCpf, isFetching: isFetchingClientePorCpf } = useClientePorCpfProposta(String(cpfRaw), false)
  const { refetch: buscarClienteAtendimentoPorCpf } = useClientePorCpfProposta(String(clienteAtendimento?.cpf), false)

  const buscouCpf = watch('dados.buscouCpf')
  const maiorDe18Anos = watch('dados.maiorDe18Anos')
  const editouAssinaturaNaEdicao = watch('editouTipoAssinatura')
  const conjuges = compradores
    .flatMap((comprador) => comprador.vinculos)
    .filter((vinculo) => vinculo.tipo === 'Conjuge')
  const nacionalidadePrincipalId = nacionalidades[0]?.id ?? ''

  const { handlers, modalOpen } = useDadosCompradorPf({
    onNext,
    nacionalidadePrincipalId,
    clienteDoAtendimento: clienteAtendimento,
    compradores,
    buscarPorId: buscarPorId,
    buscarPorCpf: buscarPorCpf,
    buscarClienteAtendimentoPorCpf: buscarClienteAtendimentoPorCpf,
    emailsJaVinculados,
    conjuges,
  })

  const prefix = 'proposta:cadastro_compradores.steps.dados_comprador'

  const handleOnNext = async () => {
    if (isLast) {
      handleSubmit(onFinish)()
      return
    }
    await handlers.handleMenorIdade()
    await handlers.validateOnNext()
  }

  return (
    <StepPropostaRoot>
      <StepPropostaHeaderContent>
        <StepPropostaHeaderTitle>{t(`${prefix}.titulo`)}</StepPropostaHeaderTitle>
      </StepPropostaHeaderContent>
      <StepPropostaContent hasBorder>
        <div className="w-full">
          <h3 className="font-poppins neutral-800 mb-6 tracking-wide">{t(`${prefix}.subtitulo`)}</h3>
          <div className="mt-1 mb-4 grid max-w-114 grid-cols-[182px_1fr] items-end gap-4 md:grid-cols-[1fr_127px] lg:grid-cols-[362px_1fr] lg:gap-10">
            <InputField
              ref={withMask('cpf', CPF_MASK)}
              id="cpf"
              name="dados.cpf"
              control={control}
              type="text"
              label={t(`${prefix}.label.cpf`)}
              placeholder={t(`${prefix}.placeholder.cpf`)}
              disabled={buscouCpf || isEditing || editouAssinaturaNaEdicao}
              afterContent={<Icon name="search" className="text-muted-foreground" />}
            />
            <Render.If condition={!isEditing && !editouAssinaturaNaEdicao}>
              {() => {
                return (
                  <Render.If
                    condition={buscouCpf}
                    fallback={
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            isLoading={isFetchingClientePorCpf}
                            variant="outline"
                            showLoadingText={false}
                            className="mb-7 border-gray-300 bg-white hover:bg-gray-50 sm:mb-9"
                            disabled={!cpfRaw || !!errors.dados?.cpf}
                            onClick={handlers.handleBuscarCliente}
                          >
                            {t(`${prefix}.buscar`)}
                          </Button>
                        </AlertDialogTrigger>
                        {modalOpen ? (
                          <AlertDialogContent className="max-w-md gap-0 md:min-w-135">
                            <AlertDialogHeader>
                              <AlertDialogTitle className="mb-6 text-xl font-medium">
                                {t(`${prefix}.modal_vincular.titulo`)}
                              </AlertDialogTitle>
                            </AlertDialogHeader>
                            <p className="font-nunito py-2 font-normal text-neutral-700">
                              {t(`${prefix}.modal_vincular.descricao`)}
                            </p>
                            <AlertDialogFooter className="pt-6 sm:justify-end">
                              <Button variant="outline" onClick={handlers.handleNovoCadastro}>
                                {t(`${prefix}.modal_vincular.novo_cadastro`)}
                              </Button>
                              <Button type="submit" onClick={handlers.handleConfirmVinculo}>
                                {t(`${prefix}.modal_vincular.vincular`)}
                              </Button>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        ) : null}
                      </AlertDialog>
                    }
                  >
                    {() => (
                      <Button
                        isLoading={isFetchingClientePorCpf}
                        showLoadingText={false}
                        variant="ghost"
                        className="text-destructive hover:text-destructive mb-7 border-gray-300 bg-white sm:mb-9"
                        disabled={!!errors.dados?.cpf}
                        onClick={handlers.handleLimpar}
                      >
                        <span className="sm:hidden">{t(`${prefix}.limpar_mobile`)}</span>
                        <span className="hidden sm:inline">{t(`${prefix}.limpar`)}</span>
                      </Button>
                    )}
                  </Render.If>
                )
              }}
            </Render.If>
          </div>
          <div className="mb-6 border-t border-neutral-200" />
          <div className="relative flex flex-1 flex-col gap-2 md:grid md:grid-cols-1 md:gap-0">
            <InputField
              className="max-w-90"
              disabled={!buscouCpf}
              required
              control={control}
              name="dados.nome"
              label={t(`${prefix}.label.nome`)}
              placeholder={t(`${prefix}.placeholder.nome`)}
              maxLength={100}
            />
            <RadioGroupField
              disabled={!buscouCpf || isEditing}
              groupLabel={t(`${prefix}.label.maior_18_anos`)}
              className="max-w-90 gap-4"
              id="age-group-label"
              options={maiorDe18AnosOptions}
              control={control}
              name="dados.maiorDe18Anos"
            />
            <ComboboxField
              disabled={!buscouCpf}
              className="max-w-90"
              placeholder={t(`${prefix}.placeholder.selecione`)}
              name="dados.nacionalidadeId"
              defaultValue={nacionalidadePrincipalId}
              control={control}
              label={t(`${prefix}.label.nacionalidade`)}
              options={nacionalidades}
              valueProperty="id"
              labelProperty="descricao"
              hideClearButton
            />
            <ComboboxField
              disabled={!buscouCpf}
              className="max-w-90"
              placeholder={t(`${prefix}.placeholder.selecione`)}
              name="dados.profissaoId"
              control={control}
              label={t(`${prefix}.label.profissao`)}
              options={profissoes}
              valueProperty="id"
              labelProperty="descricao"
              hideClearButton
            />
            <Render.If condition={maiorDe18Anos === 'nao'}>
              {() => (
                <InputField
                  disabled={!buscouCpf}
                  className="max-w-90"
                  name="dados.email"
                  control={control}
                  label={t(`${prefix}.label.email`)}
                  placeholder={t(`${prefix}.placeholder.email`)}
                />
              )}
            </Render.If>
          </div>
        </div>
      </StepPropostaContent>
      <StepPropostaFooter>
        <Button className="font-nunito h-12 w-full md:w-auto" variant="outline" onClick={onBack}>
          {t('proposta:cadastro_compradores.steps.voltar')}
        </Button>
        <Button className="font-nunito h-12 w-full md:w-auto" onClick={handleOnNext}>
          {t(isLast ? 'proposta:cadastro_compradores.steps.concluir' : 'proposta:cadastro_compradores.steps.proximo')}
        </Button>
      </StepPropostaFooter>
    </StepPropostaRoot>
  )
}
